// Pathways v3, learner and public surfaces: the data-testid registry.
//
// Contract: docs/pathways-module/pathways-api-contract.md, §9 and §9.1. The admin lane has
// its own registry (./pathwaysAdmin.js). An id that depends on a row is a function of that
// row's id, so the component and the test cannot disagree on the separator.

export const PATHWAYS_LEARNER = Object.freeze({
  // ── /parcours: the public cards (public/Parcours.jsx, public/pathways/PathwayCard.jsx) ──
  catalogue: "pathways-catalogue",
  catalogueEmpty: "pathways-catalogue-empty",
  catalogueLoadError: "pathways-catalogue-load-error",
  card: (id) => `pathway-card-${id}`,
  cardType: (id) => `pathway-card-type-${id}`,             // "step by step" / "flexible" / "N levels"
  cardDuration: (id) => `pathway-card-duration-${id}`,     // "~N hours total" / "~N min"; absent at 0
  cardObjective: (id, i) => `pathway-card-objective-${id}-${i}`,
  cardPrice: (id) => `pathway-card-price-${id}`,           // "Free" or the formatted price
  cardSpots: (id) => `pathway-card-spots-${id}`,           // "6 spots left" / "Full"; absent when unlimited
  cardJoin: (id) => `pathway-card-join-${id}`,             // "Join the pathway"; absent when full

  // ── the join form (public/pathways/JoinForm.jsx) ──
  joinForm: (id) => `pathway-join-${id}`,
  joinName: (id) => `pathway-join-name-${id}`,
  joinNameError: (id) => `pathway-join-name-error-${id}`,
  joinEmail: (id) => `pathway-join-email-${id}`,           // locked to the session's address when signed in
  joinEmailError: (id) => `pathway-join-email-error-${id}`,
  joinSubmit: (id) => `pathway-join-submit-${id}`,         // "Enroll"
  joinError: (id) => `pathway-join-error-${id}`,           // a refused enrol / checkout, worded by pathwayErrorMessage
  joinSignedInAs: (id) => `pathway-join-signed-in-${id}`,
  joinSwitchAccount: (id) => `pathway-join-switch-${id}`,  // "Not you?"

  // ── /parcours/:id, not enrolled (public/pathways/PathwayDetail.jsx) ──
  detail: "pathway-detail",
  detailOutline: "pathway-detail-outline",
  detailLevel: (id) => `pathway-detail-level-${id}`,
  detailStep: (id) => `pathway-detail-step-${id}`,
  checkoutConfirming: "pathway-checkout-confirming",       // polling after a paid return
  checkoutSlow: "pathway-checkout-slow",                   // the poll ran out: paid, access not visible yet
  checkoutRecheck: "pathway-checkout-recheck",             // "Check again" after checkoutSlow
  checkoutUnpaid: "pathway-checkout-unpaid",               // the session came back not paid
  checkoutCancelled: "pathway-checkout-cancelled",         // ?cancel=1
  notFound: "pathway-not-found",
  notFoundSignIn: "pathway-not-found-sign-in",
  loadError: "pathway-load-error",

  // ── the pathway home (public/pathways/PathwayHomeView.jsx) ──
  home: "pathway-home",
  homeObjectives: "pathway-home-objectives",
  homeProgress: "pathway-home-progress",                   // "7 of 12 steps"
  homePercent: "pathway-home-percent",                     // "58%"
  homeProgressBar: "pathway-home-progress-bar",            // role=progressbar
  homeLevel: (id) => `pathway-home-level-${id}`,
  homeLevelTitle: (id) => `pathway-home-level-title-${id}`, // absent for successive / alternative
  homeStep: (id) => `pathway-home-step-${id}`,             // data-visual = done | current | open | locked; data-optional
  homeStepLock: (id) => `pathway-home-step-lock-${id}`,    // "Unlocks after <title>"
  homeStepOptional: (id) => `pathway-home-step-optional-${id}`,
  homeStepDuration: (id) => `pathway-home-step-duration-${id}`,
  homeStepKind: (id) => `pathway-home-step-kind-${id}`,     // data-kind = the step's kind; icon + sr-only name
  homeContinue: "pathway-home-continue",                   // "Continue · <current step>"
  homeCompleted: "pathway-home-completed",                 // "You completed the pathway"
  homeCertificate: "pathway-home-certificate",             // "Download certificate"
  homePrompts: "pathway-home-prompts",
  homePrompt: (id) => `pathway-home-prompt-${id}`,

  // ── the step screen (public/pathways/StepScreenView.jsx) ──
  step: "pathway-step",
  stepEyebrow: "pathway-step-eyebrow",                     // "Level 1 · Step 2 of 4"
  stepBack: "pathway-step-back",
  stepDone: "pathway-step-done",                           // the "Done" state of a completed step
  stepVideo: "pathway-step-video",                         // <video> or <iframe>
  stepVideoHint: "pathway-step-video-hint",                // "Auto-completes at 90% watched"
  stepVideoFallback: "pathway-step-video-fallback",        // progress unobservable: explanation + stepMarkDone
  stepReading: "pathway-step-reading",
  stepPdf: "pathway-step-pdf",                             // <a> to the gated PDF, new tab
  stepMarkDone: "pathway-step-mark-done",
  stepError: "pathway-step-error",
  stepResources: "pathway-step-resources",
  stepResource: (i) => `pathway-step-resource-${i}`,
  stepNext: "pathway-step-next",                           // <a>/<button> "Next: <title> →"; data-locked when locked
  stepLocked: "pathway-step-locked",                       // the 409 STEP_LOCKED screen

  // ── a practice step (public/pathways/StepPractice.jsx, contract §10.3, §10.7) ──
  practice: "pathway-practice",
  practiceInstructions: "pathway-practice-instructions",
  practiceForm: "pathway-practice-form",                   // only when submission_required
  practiceText: "pathway-practice-text",                   // <textarea>
  practicePhoto: "pathway-practice-photo",                 // <input type=file>
  practicePhotoName: "pathway-practice-photo-name",        // the picked file's name
  practicePhotoRemove: "pathway-practice-photo-remove",
  practiceSubmit: "pathway-practice-submit",
  practiceError: "pathway-practice-error",                 // client refusal (type / size / empty) or a worded server refusal
  practiceSubmitted: "pathway-practice-submitted",         // "Submitted. Your practitioner will review it."
  practiceSubmissions: "pathway-practice-submissions",
  practiceSubmission: (id) => `pathway-practice-submission-${id}`,
  practiceSubmissionStatus: (id) => `pathway-practice-submission-status-${id}`, // data-status
  practiceSubmissionFeedback: (id) => `pathway-practice-submission-feedback-${id}`,
  practiceSubmissionPhoto: (id) => `pathway-practice-submission-photo-${id}`,   // <img>

  // ── a live step (public/pathways/StepLive.jsx, contract §10.4, §10.7) ──
  live: "pathway-live",                                    // data-mode = cohort | self_paced
  liveWhen: "pathway-live-when",                           // cohort: the instant in the browser zone
  liveZone: "pathway-live-zone",                           // "Shown in your time zone (<IANA zone>)"
  liveDuration: "pathway-live-duration",
  liveLocation: "pathway-live-location",
  liveCalendar: "pathway-live-calendar",                   // <a> to calendar.ics; aria-disabled in preview
  liveAttended: "pathway-live-attended",                   // "Attended ✓"
  liveService: "pathway-live-service",                     // self-paced: the session type card
  liveServiceUnavailable: "pathway-live-service-unavailable",
  liveBook: "pathway-live-book",                           // <a> to booking_path on the tenant site; aria-disabled in preview
  liveUpcoming: "pathway-live-upcoming",                   // "Booked for <date>"
  liveCompletesHint: "pathway-live-completes-hint",
  liveCompletedBooking: "pathway-live-completed-booking",

  // ── a course step (public/pathways/StepCourse.jsx, contract §10.5, §10.7) ──
  course: "pathway-course",
  courseCover: "pathway-course-cover",
  courseLessons: "pathway-course-lessons",                 // "N of M lessons"
  courseOpen: "pathway-course-open",                       // <a> to player_path; aria-disabled in preview
  courseUnavailable: "pathway-course-unavailable",         // the course is not published

  // ── the quiz of a step (public/pathways/StepQuiz.jsx) ──
  quiz: "pathway-quiz",
  quizChoice: (questionId, i) => `pathway-quiz-choice-${questionId}-${i}`,
  quizQuestionResult: (questionId) => `pathway-quiz-question-result-${questionId}`,
  quizSubmit: "pathway-quiz-submit",
  quizRetry: "pathway-quiz-retry",                         // "Try again"
  quizResult: "pathway-quiz-result",                       // data-passed = true | false
  quizAttempts: "pathway-quiz-attempts",
  quizError: "pathway-quiz-error",

  // ── /parcours/nudges-off (public/pathways/NudgesOff.jsx) ──
  nudgesOff: "pathway-nudges-off",
  nudgesOffDone: "pathway-nudges-off-done",
  nudgesOffInvalid: "pathway-nudges-off-invalid",
  nudgesOffError: "pathway-nudges-off-error",

  // ── /compte "My pathways" (public/pathways/PortalPathways.jsx) ──
  portalSection: "client-pathways",
  portalEmpty: "client-pathways-empty",
  portalLoadError: "client-pathways-load-error",
  portalCard: (id) => `client-pathway-${id}`,
  portalContinue: (id) => `client-pathway-continue-${id}`, // <a>: the current step's screen, or the home
  portalPercent: (id) => `client-pathway-percent-${id}`,
});
